import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, Alert, Snackbar, Typography } from '@mui/material';
import { getDb, waitForFirebase } from '../services/firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

interface TeamDialogProps {
  open: boolean;
  onClose: () => void;
  onGroupCreated?: () => void;
}

const TeamDialog: React.FC<TeamDialogProps> = ({ open, onClose, onGroupCreated }) => {
  const { user } = useAuth();
  const [teamForm, setTeamForm] = useState({
    name: '',
    taxId: '',
    adminEmails: '',
    members: ''
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState('');

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTeamForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = () => {
    if (!teamForm.name) {
      setError('群組名稱必填');
      return;
    }
    setError('');
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      // 🔧 修復：等待 Firebase 初始化並獲取 db 實例
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }
      
      // 檢查群組名稱是否重複
      const nameToCheck = teamForm.name.trim();
      const q = query(collection(db, 'groups'), where('name', '==', nameToCheck));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setSnackbar({ open: true, message: '已有相同名稱，請更換其他名稱！', severity: 'error' });
        setSubmitting(false);
        return;
      }
      // 解析管理員與成員
      const currentUserEmail = (user?.email || '').toLowerCase(); // 🔧 修復：統一轉小寫
      const adminArr = teamForm.adminEmails.split(/,|\n/).map(e => e.trim().toLowerCase()).filter(Boolean); // 🔧 修復：轉小寫
      const allAdmins = Array.from(new Set([currentUserEmail, ...adminArr]));
      const memberArr = teamForm.members.split(/,|\n/).map(e => e.trim().toLowerCase()).filter(Boolean); // 🔧 修復：轉小寫
      const members = [
        ...allAdmins.map(email => ({ email, name: '', isAdmin: true, profile: '', joinedAt: new Date() })),
        ...memberArr.filter(email => !allAdmins.includes(email)).map(email => ({ email, name: '', isAdmin: false, profile: '', joinedAt: new Date() }))
      ];
      const admins = members.filter(m => m.isAdmin).map(m => m.email.toLowerCase()); // 🔧 修復：轉小寫
      const membersEmail = members.map(m => m.email.toLowerCase()); // 🔧 修復：轉小寫
      await addDoc(collection(db, 'groups'), {
        name: teamForm.name,
        taxId: teamForm.taxId,
        admins,
        members,
        membersEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      if (onGroupCreated) onGroupCreated();
      setSnackbar({ open: true, message: '成功建立好友群', severity: 'success' });
      setShowConfirm(false);
      setTeamForm({ name: '', taxId: '', adminEmails: '', members: '' });
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (e) {
      console.error('❌ 建立群組失敗:', e);
      setSnackbar({ open: true, message: '建立群組失敗，請稍後再試', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 24, color: '#1976d2', letterSpacing: 1, pt: 4, pb: 1 }}>
          建立群組
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2, textAlign: 'center', color: '#1976d2', fontWeight: 700 }}>
            群組之間能互相讀取影片
          </Box>
          <TextField label="取一個好記的名稱，如：專案/公司/興趣/好友" name="name" value={teamForm.name} onChange={handleInput} fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="(選填) 統一編號 (for 企業交片區)" name="taxId" value={teamForm.taxId} onChange={handleInput} fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="(選填) 群組管理員 Email（多位請用逗號分隔）" name="adminEmails" value={teamForm.adminEmails} onChange={handleInput} fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="(選填) 好友名單 (多位請用逗號分隔 Email)" name="members" value={teamForm.members} onChange={handleInput} fullWidth size="small" sx={{ mb: 2 }} />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">關閉</Button>
          <Button variant="contained" color="primary" onClick={handleCreate} disabled={submitting}>建立</Button>
        </DialogActions>
      </Dialog>
      {/* 確認 Dialog */}
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 22, color: '#1976d2', letterSpacing: 1, pt: 3, pb: 1 }}>
          請確認群組資訊
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}><b>群組名稱：</b>{teamForm.name}</Typography>
          <Typography sx={{ mb: 1 }}><b>統一編號：</b>{teamForm.taxId ? teamForm.taxId : '暫不填寫'}</Typography>
          <Typography sx={{ mb: 1 }}><b>管理員：</b>{teamForm.adminEmails ? teamForm.adminEmails : '暫不填寫'}</Typography>
          <Typography sx={{ mb: 1 }}><b>好友名單：</b>{teamForm.members ? teamForm.members : '暫不填寫'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)} color="primary">返回修改</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} disabled={submitting}>確定</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TeamDialog; 